import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import {db} from "../../Security/firebase-config";
import {ackLiveLobbySegmentPlayback} from "../../utils/liveLobby";

const DEFAULT_MIME_TYPE = "video/webm";

function buildOverlayLabel(args) {
  if (args.blockedMessage) return args.blockedMessage;
  if (args.unsupported) {
    return "This browser cannot play segmented live-lobby video in the current playback mode.";
  }
  if (args.liveMode && args.waitingForBuffer) {
    return "Waiting for enough live segments to buffer the stream...";
  }
  if (!args.started && args.segmentCount === 0) {
    return args.liveMode ?
      "Waiting for the host to upload the first live segment..." :
      "Preparing segmented VOD playback...";
  }
  if (args.liveMode && args.finalizing && args.segmentCount === 0) {
    return "This live lobby is finalizing. Playback will appear as soon as the first segment is available.";
  }
  return "";
}

export default function SegmentPlaylistVideo({
  teamId,
  mapId,
  videoId,
  lobbyId = null,
  liveMode = false,
  lobbyStatus = null,
  playbackDelayMs = 12000,
  muted = false,
  autoPlay = true,
  className = "",
  onCurrentTimeChange,
  onPlaybackBlocked,
  onPlaybackReady,
}) {
  const [segments, setSegments] = useState([]);
  const [unsupported, setUnsupported] = useState(false);
  const [blockedMessage, setBlockedMessage] = useState("");
  const [started, setStarted] = useState(false);
  const [waitingForBuffer, setWaitingForBuffer] = useState(liveMode);
  const [sourceReady, setSourceReady] = useState(false);

  const videoRef = useRef(null);
  const mediaSourceRef = useRef(null);
  const sourceBufferRef = useRef(null);
  const objectUrlRef = useRef("");
  const queuedSegmentRef = useRef(null);
  const appendedSegmentIdsRef = useRef(new Set());
  const chargedSegmentIdsRef = useRef(new Set());
  const anchorIndexRef = useRef(null);
  const isAppendingRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    setStarted(false);
    setBlockedMessage("");
    setWaitingForBuffer(liveMode);
    setUnsupported(false);
  }, [liveMode, lobbyId, mapId, teamId, videoId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!teamId || !mapId || !videoId) {
      setSegments([]);
      return undefined;
    }

    const segmentQuery = query(
      collection(db, "teams", teamId, "maps", mapId, "videos", videoId, "segments"),
      orderBy("index", "asc"),
    );

    return onSnapshot(segmentQuery, (snapshot) => {
      const nextSegments = snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setSegments(nextSegments);
    });
  }, [mapId, teamId, videoId]);

  const effectiveAnchorIndex = useMemo(() => {
    if (anchorIndexRef.current != null) return anchorIndexRef.current;
    if (!segments.length) return null;

    if (!liveMode) {
      anchorIndexRef.current = 0;
      return anchorIndexRef.current;
    }

    const desiredDelaySegments = Math.max(
      2,
      Math.ceil(Math.max(0, playbackDelayMs) / 4000),
    );
    const canStartEarly = ["finalizing", "finalized", "ended", "failed"].includes(String(lobbyStatus || ""));
    if (segments.length < desiredDelaySegments && !canStartEarly) {
      return null;
    }

    anchorIndexRef.current = Math.max(0, segments.length - desiredDelaySegments);
    return anchorIndexRef.current;
  }, [liveMode, lobbyStatus, playbackDelayMs, segments]);

  const playableSegments = useMemo(() => {
    if (effectiveAnchorIndex == null) return [];
    return segments.filter((segment) => Number(segment.index) >= effectiveAnchorIndex);
  }, [effectiveAnchorIndex, segments]);

  const finalizing = ["finalizing", "finalized", "ended"].includes(String(lobbyStatus || ""));
  const timelineOffsetSec = useMemo(() => {
    const firstSegment = playableSegments[0];
    if (!firstSegment) return 0;
    return Math.max(0, Number(firstSegment.startOffsetMs || 0) / 1000);
  }, [playableSegments]);

  useEffect(() => {
    if (typeof onCurrentTimeChange !== "function" || !videoRef.current) return undefined;

    const video = videoRef.current;
    const handleTimeUpdate = () => {
      onCurrentTimeChange(Number((timelineOffsetSec + Number(video.currentTime || 0)).toFixed(3)));
    };

    video.addEventListener("timeupdate", handleTimeUpdate);
    return () => {
      video.removeEventListener("timeupdate", handleTimeUpdate);
    };
  }, [onCurrentTimeChange, timelineOffsetSec]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    if (!window.MediaSource) {
      setUnsupported(true);
      return undefined;
    }

    const mediaSource = new window.MediaSource();
    const video = videoRef.current;
    mediaSourceRef.current = mediaSource;
    const objectUrl = URL.createObjectURL(mediaSource);
    objectUrlRef.current = objectUrl;

    if (video) {
      video.src = objectUrl;
    }

    const handleSourceOpen = () => {
      setSourceReady(true);
    };
    mediaSource.addEventListener("sourceopen", handleSourceOpen);

    return () => {
      mediaSource.removeEventListener("sourceopen", handleSourceOpen);
      try {
        sourceBufferRef.current?.abort?.();
      } catch {}
      sourceBufferRef.current = null;
      mediaSourceRef.current = null;
      setSourceReady(false);
      appendedSegmentIdsRef.current = new Set();
      chargedSegmentIdsRef.current = new Set();
      anchorIndexRef.current = null;
      queuedSegmentRef.current = null;
      isAppendingRef.current = false;

      if (video) {
        try {
          video.pause?.();
          video.removeAttribute("src");
          video.load?.();
        } catch {}
      }

      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = "";
      }
    };
  }, [lobbyId, mapId, teamId, videoId]);

  useEffect(() => {
    const mediaSource = mediaSourceRef.current;
    if (!sourceReady || !mediaSource || mediaSource.readyState !== "open") return;
    if (sourceBufferRef.current) return;

    const firstSegment = playableSegments[0];
    if (!firstSegment) return;

    const mimeType = String(firstSegment.mimeType || DEFAULT_MIME_TYPE);
    try {
      const sourceBufferMime =
        window.MediaSource.isTypeSupported?.(mimeType) ? mimeType : DEFAULT_MIME_TYPE;
      const sourceBuffer = mediaSource.addSourceBuffer(sourceBufferMime);
      sourceBuffer.mode = "sequence";
      sourceBufferRef.current = sourceBuffer;
      sourceBuffer.addEventListener("updateend", () => {
        const queuedSegment = queuedSegmentRef.current;
        if (queuedSegment) {
          appendedSegmentIdsRef.current.add(queuedSegment.id);
          queuedSegmentRef.current = null;
          setStarted(true);
          setWaitingForBuffer(false);
        }
        isAppendingRef.current = false;
      });
    } catch (error) {
      console.error("[SegmentPlaylistVideo] Failed to create source buffer:", error);
      setUnsupported(true);
    }
  }, [playableSegments, sourceReady]);

  useEffect(() => {
    if (unsupported || blockedMessage) return undefined;
    if (!mediaSourceRef.current || !sourceBufferRef.current) return undefined;
    if (!playableSegments.length) return undefined;

    let cancelled = false;

    async function pumpQueue() {
      if (cancelled) return;
      if (isAppendingRef.current) return;
      if (!sourceBufferRef.current || sourceBufferRef.current.updating) return;

      const nextSegment = playableSegments.find(
        (segment) => !appendedSegmentIdsRef.current.has(segment.id),
      );

      if (!nextSegment) {
        const shouldFinalize =
          finalizing &&
          mediaSourceRef.current?.readyState === "open" &&
          playableSegments.length > 0;
        if (shouldFinalize) {
          try {
            mediaSourceRef.current.endOfStream();
          } catch {}
        }
        return;
      }

      isAppendingRef.current = true;

      try {
        if (lobbyId && !chargedSegmentIdsRef.current.has(nextSegment.id)) {
          const ack = await ackLiveLobbySegmentPlayback({
            teamId,
            lobbyId,
            segmentId: nextSegment.id,
          });

          if (ack?.allowed === false) {
            const message =
              ack?.message ||
              "Your personal egress allowance for this billing cycle has been reached.";
            setBlockedMessage(message);
            onPlaybackBlocked?.(message);
            isAppendingRef.current = false;
            return;
          }

          chargedSegmentIdsRef.current.add(nextSegment.id);
        }

        const response = await fetch(nextSegment.videoURL);
        if (!response.ok) {
          throw new Error(`Failed to fetch segment ${nextSegment.id} (${response.status}).`);
        }
        const bytes = await response.arrayBuffer();

        if (cancelled || !sourceBufferRef.current) return;

        queuedSegmentRef.current = nextSegment;
        sourceBufferRef.current.appendBuffer(bytes);
      } catch (error) {
        console.error("[SegmentPlaylistVideo] Failed fetching/queueing segment:", error);
        isAppendingRef.current = false;
      }
    }

    const interval = window.setInterval(pumpQueue, 750);
    pumpQueue();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [
    blockedMessage,
    finalizing,
    liveMode,
    lobbyId,
    onPlaybackBlocked,
    playableSegments,
    teamId,
    unsupported,
  ]);

  useEffect(() => {
    if (!videoRef.current || !autoPlay || !started) return;
    videoRef.current.play?.().catch(() => {});
  }, [autoPlay, started]);

  const overlayLabel = buildOverlayLabel({
    blockedMessage,
    unsupported,
    liveMode,
    waitingForBuffer,
    started,
    segmentCount: playableSegments.length,
    finalizing,
  });

  return (
    <div
      className={className}
      style={{
        position: "relative",
        minHeight: "16rem",
        background: "rgba(3, 10, 18, 0.85)",
        borderRadius: "1rem",
        overflow: "hidden",
      }}
    >
      <video
        ref={videoRef}
        onLoadedData={onPlaybackReady}
        controls
        autoPlay={autoPlay}
        muted={muted}
        playsInline
        style={{
          width: "100%",
          height: "100%",
          minHeight: "16rem",
          background: "#02070d",
          display: "block",
        }}
      />

      {overlayLabel ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            background: "linear-gradient(180deg, rgba(2,7,13,0.35), rgba(2,7,13,0.82))",
            color: "#f3f7fb",
            textAlign: "center",
            fontWeight: 600,
            pointerEvents: "none",
          }}
        >
          <div style={{maxWidth: "34rem"}}>{overlayLabel}</div>
        </div>
      ) : null}
    </div>
  );
}
